import { bytesEqual, encodeBase64URL, randomBytes, utf8 } from "./bytes.js";
import { BindId } from "./endpoint.js";
import { BlindBindError, asBlindBindError } from "./errors.js";
import { type HostIdentity, identityMaterial } from "./identity.js";
import {
  FrameType,
  decodeEncryptedFrame,
  decodeHello,
  decryptFramePayload,
  deriveSessionKeys,
  encodeHello,
  encryptFramePayload,
  sessionIDFromTranscript,
  transcriptHash,
} from "./protocol.js";
import type { SocketInbox, WebSocketLike } from "./websocket.js";

const clientFinished = utf8("client-finished");
const hostFinished = utf8("host-finished");

export interface HandshakeResult {
  sessionId: string;
  sendKey: CryptoKey;
  receiveKey: CryptoKey;
}

export async function clientHandshake(
  socket: WebSocketLike,
  inbox: SocketInbox,
  bindId: BindId,
  options: { signal?: AbortSignal; timeoutMs: number },
): Promise<HandshakeResult> {
  try {
    const encodedServerHello = await inbox.nextBytes(options);
    const serverHello = decodeHello(encodedServerHello, FrameType.ServerHello);
    const actualBindID = await BindId.fromPublicKey(serverHello.publicKey);
    if (!actualBindID.equals(bindId))
      throw new BlindBindError("identity_mismatch", "Relay connected to a different host identity");
    const hostPublicKey = await crypto.subtle.importKey(
      "spki",
      serverHello.publicKey,
      { name: "ECDH", namedCurve: "P-256" },
      false,
      [],
    );
    const ephemeral = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveBits"]);
    if (!("privateKey" in ephemeral) || !("publicKey" in ephemeral))
      throw new Error("WebCrypto did not return an ECDH key pair");
    const ephemeralPublic = new Uint8Array(await crypto.subtle.exportKey("raw", ephemeral.publicKey));
    const encodedClientHello = encodeHello(FrameType.ClientHello, randomBytes(32), ephemeralPublic);
    socket.send(encodedClientHello);
    const sharedSecret = new Uint8Array(
      await crypto.subtle.deriveBits({ name: "ECDH", public: hostPublicKey }, ephemeral.privateKey, 256),
    );
    const hash = await transcriptHash(encodedServerHello, encodedClientHello);
    const keys = await deriveSessionKeys(sharedSecret, hash);
    sharedSecret.fill(0);
    socket.send(await encryptFramePayload(keys.clientToHost, FrameType.Finish, 0n, clientFinished, hash));
    const hostFinish = decodeEncryptedFrame(await inbox.nextBytes(options), FrameType.Finish);
    if (
      hostFinish.sequence !== 0n ||
      !bytesEqual(await decryptFramePayload(keys.hostToClient, hostFinish, hash), hostFinished)
    ) {
      throw new BlindBindError("handshake_failed", "Host handshake confirmation is invalid");
    }
    return {
      sessionId: encodeBase64URL(sessionIDFromTranscript(hash)),
      sendKey: keys.clientToHost,
      receiveKey: keys.hostToClient,
    };
  } catch (error) {
    const normalized = asHandshakeError(error);
    socket.close(4400, normalized.message.slice(0, 120));
    throw normalized;
  }
}

export async function hostHandshake(
  socket: WebSocketLike,
  inbox: SocketInbox,
  identity: HostIdentity,
  options: { signal?: AbortSignal; timeoutMs: number },
): Promise<HandshakeResult> {
  try {
    const material = identityMaterial(identity);
    const encodedServerHello = encodeHello(FrameType.ServerHello, randomBytes(32), material.publicKeySPKI);
    socket.send(encodedServerHello);
    const encodedClientHello = await inbox.nextBytes(options);
    const clientHello = decodeHello(encodedClientHello, FrameType.ClientHello);
    const clientPublicKey = await crypto.subtle.importKey(
      "raw",
      clientHello.publicKey,
      { name: "ECDH", namedCurve: "P-256" },
      false,
      [],
    );
    const sharedSecret = new Uint8Array(
      await crypto.subtle.deriveBits({ name: "ECDH", public: clientPublicKey }, material.privateKey, 256),
    );
    const hash = await transcriptHash(encodedServerHello, encodedClientHello);
    const keys = await deriveSessionKeys(sharedSecret, hash);
    sharedSecret.fill(0);
    const clientFinish = decodeEncryptedFrame(await inbox.nextBytes(options), FrameType.Finish);
    if (
      clientFinish.sequence !== 0n ||
      !bytesEqual(await decryptFramePayload(keys.clientToHost, clientFinish, hash), clientFinished)
    ) {
      throw new BlindBindError("handshake_failed", "Client handshake confirmation is invalid");
    }
    socket.send(await encryptFramePayload(keys.hostToClient, FrameType.Finish, 0n, hostFinished, hash));
    return {
      sessionId: encodeBase64URL(sessionIDFromTranscript(hash)),
      sendKey: keys.hostToClient,
      receiveKey: keys.clientToHost,
    };
  } catch (error) {
    const normalized = asHandshakeError(error);
    socket.close(4400, normalized.message.slice(0, 120));
    throw normalized;
  }
}

function asHandshakeError(error: unknown): BlindBindError {
  const normalized = asBlindBindError(error, "handshake_failed");
  if (normalized.code === "connection_failed" || normalized.code === "offline" || normalized.code === "busy")
    return normalized;
  if (normalized.code === "identity_mismatch" || normalized.code === "protocol_error" || normalized.code === "closed")
    return normalized;
  return new BlindBindError("handshake_failed", normalized.message, { cause: normalized });
}
